<template>
  <v-card variant="outlined">
    <div class="lx-scroll-x">
      <v-table class="lx-results">
        <caption class="lx-sr-only">Weekly forecast by indicator. First column is the last observation.</caption>
        <thead>
          <tr>
            <th scope="col" class="text-left">Indicator</th>
            <th v-for="d in dates" :key="d" scope="col" class="text-center"><div style="width: 100px">{{ d }}</div></th>
          </tr>
        </thead>
        <tbody>
          <template v-for="row in rows" :key="row.name">
            <tr>
              <th scope="row" class="text-left lx-rowhead">
                {{ row.name }}
                <span v-if="unitOf(row.name)" class="lx-unit">{{ unitOf(row.name) }}</span>
              </th>
              <td v-for="(v, i) in row.data" :key="i" class="text-center">{{ fmt(row.name, v) }}</td>
            </tr>
            <!-- Interval sub-rows (the table is date-wise, so Lower/Upper are rows, not columns) -->
            <template v-if="showIntervals && intervalFor(row.name)">
              <tr class="lx-subrow">
                <th scope="row" class="text-left lx-rowhead lx-rowhead--sub">↳ Lower · {{ intervalFor(row.name).level }}% interval</th>
                <td v-for="(d, i) in dates" :key="'lo' + i" class="text-center">{{ fmt(row.name, intervalFor(row.name).lower[i]) }}</td>
              </tr>
              <tr class="lx-subrow">
                <th scope="row" class="text-left lx-rowhead lx-rowhead--sub">↳ Upper · {{ intervalFor(row.name).level }}% interval</th>
                <td v-for="(d, i) in dates" :key="'hi' + i" class="text-center">{{ fmt(row.name, intervalFor(row.name).upper[i]) }}</td>
              </tr>
            </template>
            <!-- Δ vs baseline when the shown result is an alternative scenario -->
            <tr v-if="deltaFor(row.name)" class="lx-subrow lx-subrow--delta">
              <th scope="row" class="text-left lx-rowhead lx-rowhead--sub">↳ Δ vs Janus baseline</th>
              <td v-for="(d, i) in dates" :key="'dl' + i" class="text-center" :class="deltaClass(deltaFor(row.name)[i])">
                {{ fmtDelta(row.name, deltaFor(row.name)[i]) }}
              </td>
            </tr>
          </template>
        </tbody>
      </v-table>
    </div>
    <div v-if="showIntervals && !hasAnyInterval" class="lx-method pa-3">
      Prediction intervals are not available for this result ({{ intervalsReason || "not provided by the backend" }}).
    </div>
  </v-card>
</template>

<script>
import { formatValue, formatDiff, unitOf } from "../lib/format";

export default {
  name: "ForecastResultsTable",
  props: {
    dates: { type: Array, default: () => [] },
    rows: { type: Array, default: () => [] }, // [{name, data:[number]}]
    baselineByDate: { type: Object, default: null }, // {label: {date: value}} — Janus baseline for Δ
    showDelta: { type: Boolean, default: false },
    intervals: { type: Object, default: null }, // {label: {level, lower:[], upper:[]}} aligned to `dates`
    showIntervals: { type: Boolean, default: false },
    intervalsReason: { type: String, default: "" },
  },
  computed: {
    hasAnyInterval() {
      return !!(this.intervals && this.rows.some((r) => this.intervals[r.name]));
    },
  },
  methods: {
    unitOf,
    fmt(label, v) {
      return formatValue(label, v);
    },
    intervalFor(label) {
      return this.intervals && this.intervals[label] ? this.intervals[label] : null;
    },
    deltaFor(label) {
      if (!this.showDelta || !this.baselineByDate || !this.baselineByDate[label]) return null;
      const row = this.rows.find((r) => r.name === label);
      if (!row) return null;
      const base = this.baselineByDate[label];
      return this.dates.map((d, i) => {
        const b = base[d];
        const v = row.data[i];
        return b == null || v == null ? null : v - Number(b);
      });
    },
    fmtDelta(label, v) {
      return formatDiff(label, v);
    },
    deltaClass(v) {
      if (v == null || v === 0) return "";
      return v > 0 ? "lx-pos" : "lx-neg";
    },
  },
};
</script>

<style scoped>
.lx-results thead th:first-child,
.lx-results tbody th.lx-rowhead {
  position: sticky; left: 0; z-index: 1; min-width: 260px;
  box-shadow: inset -1px 0 0 var(--line);
}
.lx-results thead th:first-child { background: var(--bg-2); }
.lx-results tbody th.lx-rowhead { background: var(--bg-1); font-family: var(--f-body); font-weight: 500; color: var(--text); text-transform: none; letter-spacing: 0; font-size: 13px; white-space: nowrap; }
.lx-results tbody th.lx-rowhead--sub { font-weight: 400; color: var(--text-dim); font-size: 12px; padding-left: 22px !important; }
.lx-results tr.lx-subrow td { color: var(--text-dim); font-size: 12px; }
.lx-results tr.lx-subrow--delta td { font-size: 12px; }
</style>
